import React, {
  createContext,
  useReducer,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { CartItem } from "../../Interfaces/Types";
import { ImprovedParticipant } from "../../Interfaces/FundraiserRole";
import config from "../../config";
import { calculateShippingCost } from "../../CommonFunctions";

export class ParticipantMismatchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ParticipantMismatchError";
  }
}

const DONATION_ID = config.DONATION_ITEM_ID;
const BUYOUT_ID = config.BUYOUT_ITEM_ID;

type CartAction =
  | {
      type: "ADD_ITEM";
      payload: { item: CartItem; participant: ImprovedParticipant };
    }
  | { type: "REMOVE_ITEM"; payload: string }
  | { type: "CLEAR_CART" }
  | {
      type: "SET_FUNDRAISER_PARTICIPANT";
      payload: ImprovedParticipant | null;
    }
  | {
      type: "UPDATE_ITEM_QUANTITY";
      payload: { id: string; quantity: number };
    }
  | {
      type: "SHOW_POPUP";
      payload: { header: string; body: string };
    }
  | {
      type: "HIDE_POPUP";
    }
  | {
      type: "REMOVE_POPUP_ERROR";
    };

export interface CartState {
  fundraiser_participant: ImprovedParticipant | null;
  cart: CartItem[];
  popup: {
    isVisible: boolean;
    header: string;
    body: React.ReactNode;
  };
  error?: string[];
}

const getInitialState = (): CartState => {
  const storedState = localStorage.getItem("cart");
  return storedState
    ? JSON.parse(storedState)
    : {
        fundraiser_participant: null,
        cart: [],
        popup: {
          isVisible: false,
          header: "",
          body: "",
        },
      };
};

const CartContext = createContext<
  | {
      state: CartState;
      dispatch: React.Dispatch<CartAction>;
      isDrawerOpen: boolean;
      setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
      toggleDrawer: () => void;
      subtotal: number;
      totalItems: number;
      showPopup: (header: string, body: string) => void;
      hidePopup: () => void;
      popup: { isVisible: boolean; header: string; body: React.ReactNode };
      baseShippingCost: number;
      freeShippingThreshold: number;
      freeShippingExceptions: string;
      baseShippingOverride: string;
      nonDonationItemTotal: number;
      nonDonationSubTotal: number;
    }
  | undefined
>(undefined);

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case "ADD_ITEM": {
      const { item, participant } = action.payload;

      if (state.cart.length === 0) {
        return {
          ...state,
          cart: [item],
          fundraiser_participant: participant,
          error: undefined,
        };
      }

      const currentParticipant = state.fundraiser_participant;
      if (
        currentParticipant?.participant_code !== participant.participant_code
      ) {
        const bodyText = `You're trying to add an item from ${participant.full_name}'s store,
        but your cart contains items from ${currentParticipant?.full_name}'s store.

        Please complete your current purchase or empty your cart before adding items from a different participant.`;

        return {
          ...state,
          popup: {
            isVisible: true,
            header: "Different Participant Detected",
            body: bodyText,
          },
          error: [
            bodyText,
            currentParticipant?.full_name || "",
            currentParticipant?.participant_code || "",
          ],
        };
      }

      // If there are no items in the cart, set the participant
      const updatedParticipant =
        state.cart.length === 0 ? participant : state.fundraiser_participant;

      let updatedCart;

      // Check if the cart already contains a donation item
      const existingDonation = state.cart.find(
        (cartItem) => cartItem.sku === config.DONATION_ITEM_ID
      );

      if (existingDonation && item.sku === config.DONATION_ITEM_ID) {
        // Update the existing donation item by adding the new donation's price
        updatedCart = state.cart.map((cartItem) =>
          cartItem.sku === config.DONATION_ITEM_ID
            ? {
                ...cartItem,
                price: existingDonation.price + item.price, // Add the new donation price to the existing donation
              }
            : cartItem
        );
      } else {
        // Add the new item to the cart (regular logic)
        const itemExists = state.cart.find(
          (cartItem) =>
            cartItem.sku === item.sku &&
            JSON.stringify(cartItem.customization) ===
              JSON.stringify(item.customization) // Check for same customization
        );

        updatedCart = itemExists
          ? state.cart.map((cartItem) =>
              cartItem.sku === item.sku &&
              JSON.stringify(cartItem.customization) ===
                JSON.stringify(item.customization)
                ? { ...cartItem, quantity: cartItem.quantity + item.quantity } // Increment quantity for matching item
                : cartItem
            )
          : [...state.cart, { ...item, quantity: item.quantity }]; // Add a new entry for items with different customization
      }

      return {
        ...state,
        cart: updatedCart,
        fundraiser_participant: updatedParticipant,
        error: undefined,
      };
    }
    case "REMOVE_ITEM": {
      const updatedCart = state.cart.filter(
        (item) => item.id !== action.payload
      );
      return { ...state, cart: updatedCart };
    }
    case "CLEAR_CART": {
      localStorage.removeItem("cart");
      return { ...state, cart: [], fundraiser_participant: null };
    }
    case "SET_FUNDRAISER_PARTICIPANT": {
      return { ...state, fundraiser_participant: action.payload };
    }
    case "UPDATE_ITEM_QUANTITY":
      return {
        ...state,
        cart: state.cart.map((item) =>
          item.id === action.payload.id
            ? {
                ...item,
                quantity:
                  item.sku === config.BUYOUT_ITEM_ID
                    ? Math.min(1, action.payload.quantity)
                    : action.payload.quantity,
              }
            : item
        ),
      };

    case "SHOW_POPUP":
      return {
        ...state,
        popup: {
          isVisible: true,
          header: action.payload.header,
          body: action.payload.body,
        },
      };

    case "HIDE_POPUP":
      return {
        ...state,
        popup: {
          ...state.popup,
          isVisible: false,
        },
      };
    case "REMOVE_POPUP_ERROR":
      return {
        ...state,
        popup: {
          isVisible: false,
          header: "",
          body: "",
        },
        error: undefined,
      };
    default:
      return state;
  }
};

const calculateSubtotal = (cart: CartItem[]): number => {
  return cart.reduce((acc, item) => acc + item.price * item.quantity, 0);
};

const calculateTotalItems = (cart: CartItem[]): number => {
  const perItemCharge = 1;
  const totalItems = cart.reduce((acc, item) => acc + item.quantity, 0);
  return cart.length > 0 ? totalItems * perItemCharge : 0;
};

const calculateTotalNonDonationItems = (cart: CartItem[]): number => {
  return cart.reduce((sum, item) => {
    if (item.sku !== DONATION_ID && item.sku !== BUYOUT_ID) {
      return sum + item.quantity;
    }
    return sum;
  }, 0);
};

const calculateDonationTotal = (cart: CartItem[]): number => {
  return cart.reduce((sum, item) => {
    if (item.sku === DONATION_ID || item.sku === BUYOUT_ID) {
      return sum + item.price * item.quantity;
    }
    return sum;
  }, 0);
};

const calculateNonDonationSubTotal = (cart: CartItem[]): number => {
  const subtotal = calculateSubtotal(cart);
  const donationTotal = calculateDonationTotal(cart);

  return subtotal - donationTotal;
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(cartReducer, getInitialState());
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(999);
  const [baseShippingCost, setBaseShippingCost] = useState(0.0);
  const [freeShippingExceptions, setFreeShippingExceptions] = useState("");
  const [baseShippingOverride, setBaseShippingOverride] = useState("");

  const toggleDrawer = () => setDrawerOpen(!isDrawerOpen);

  const subtotal = calculateSubtotal(state.cart);
  const totalItems = calculateTotalItems(state.cart);
  const nonDonationItemTotal = calculateTotalNonDonationItems(state.cart);
  const nonDonationSubTotal = calculateNonDonationSubTotal(state.cart);

  const showPopup = (header: string, body: string) => {
    dispatch({
      type: "SHOW_POPUP",
      payload: { header, body },
    });
  };

  const hidePopup = () => {
    dispatch({
      type: "HIDE_POPUP",
    });
  };

  useEffect(() => {
    const fetchFreeShippingThreshold = async () => {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}config-options/free_shipping_threshold`
        );
        const data = await response.json();
        setFreeShippingThreshold(Number(data[0].config_value));
      } catch (error) {
        console.error("Error fetching free shipping threshold:", error);
      }
    };
    const fetchBaseShipping = async () => {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}config-options/base_shipping_cost`
        );
        const data = await response.json();
        setBaseShippingCost(Number(data[0].config_value));
      } catch (error) {
        console.error("Error fetching base shipping:", error);
      }
    };
    const fetchBaseShippingOverride = async () => {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}config-options/base_shipping_bypass`
        );
        const data = await response.json();
        setBaseShippingOverride(data[0].config_value);
      } catch (error) {
        console.error("Error fetching base shipping override:", error);
      }
    };

    const fetchFreeShippingException = async () => {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}config-options/free_shipping_bypass`
        );
        const data = await response.json();
        setFreeShippingExceptions(data[0].config_value);
      } catch (error) {
        console.error("Error fetching shipping exceptions:", error);
      }
    };

    fetchBaseShipping();
    fetchFreeShippingThreshold();
    fetchBaseShippingOverride();
    fetchFreeShippingException();
  }, []);

  // Effect to update localStorage whenever the cart state changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(state));
  }, [state]);

  // Clear the cart when the entire page is unloaded
  // useEffect(() => {
  //   const handleBeforeUnload = () => {
  //     localStorage.removeItem("cart"); // Clear cart from localStorage
  //     dispatch({ type: "CLEAR_CART" }); // Update context state
  //   };

  //   // Add event listener for the beforeunload event
  //   window.addEventListener("beforeunload", handleBeforeUnload);

  //   return () => {
  //     // Clean up the event listener
  //     window.removeEventListener("beforeunload", handleBeforeUnload);
  //   };
  // }, [dispatch]);

  return (
    <CartContext.Provider
      value={{
        state,
        dispatch,
        isDrawerOpen,
        setDrawerOpen,
        toggleDrawer,
        subtotal,
        totalItems,
        showPopup,
        hidePopup,
        popup: state.popup,
        baseShippingCost,
        freeShippingThreshold,
        freeShippingExceptions,
        baseShippingOverride,
        nonDonationItemTotal,
        nonDonationSubTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
